import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useInvitation } from '../contexts/InvitationContext';
import { useToast } from '../contexts/ToastContext';
import { userAPI } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import { TextInput } from '../components/ui';
import { Check } from 'react-feather';

const InvitationWelcome: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { isAuthenticated, user, sessionToken, logout } = useAuth();
  const { invitationInfo, acceptInvitation, isProcessing, error, clearInvitation } = useInvitation();
  const toast = useToast();
  const [hasProcessed, setHasProcessed] = useState(false);
  // Inline guardian intake (BUG-pack "C" from the invite redesign).
  // Guardians joining an existing athlete's family have nothing to fill in
  // during quickstart, so we capture name + phone right here on the accept
  // page and skip onboarding entirely for them. Athletes still go through
  // quickstart since they need their own profile.
  // Invitee picks their own role on the accept page. The inviter has
  // no UI to set anticipated_user_type, so we never pre-pick — even
  // the column on family_invitations is effectively dead today. Always
  // start unselected and force a fresh pick.
  const [selectedRole, setSelectedRole] = useState<'athlete' | 'guardian' | null>(null);
  const isGuardianInvite = selectedRole === 'guardian';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');

  // Soft warn (not hard block) when the logged-in user's email doesn't
  // match the invitation. Apple Hide-My-Email and multi-Google-account
  // users would be permanently trapped by a hard block — they have no
  // way to "log in as the invited email" because that Auth0 identity
  // doesn't exist. Show the inviter's intent + an explicit "continue as
  // {user}" confirm, but allow it.
  const emailMismatch = useMemo(() => {
    if (!isAuthenticated || !user?.email || !invitationInfo?.email) return false;
    return user.email.toLowerCase() !== invitationInfo.email.toLowerCase();
  }, [isAuthenticated, user?.email, invitationInfo?.email]);
  const [mismatchAcknowledged, setMismatchAcknowledged] = useState(false);

  const handleAcceptInvitation = async () => {
    if (!sessionToken) {
      toast.error('Authentication required to accept invitation');
      return;
    }

    if (!user?.email) {
      toast.error('User information required to accept invitation');
      return;
    }

    if (emailMismatch && !mismatchAcknowledged) {
      toast.error('Confirm continuing with this account before accepting.');
      return;
    }

    if (!selectedRole) {
      toast.error('Choose Athlete or Parent / Guardian to continue.');
      return;
    }

    if (isGuardianInvite && (!firstName.trim() || !lastName.trim())) {
      toast.error('Please enter your first and last name to continue.');
      return;
    }

    setHasProcessed(true);
    try {
      const result = await acceptInvitation(sessionToken, {
        name: user.email.split('@')[0], // Use email username as fallback name
        email: user.email,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        phone: phone.trim() || undefined,
        selected_user_type: selectedRole,
      });

      if (result.ok) {
        toast.success(`Welcome to ${invitationInfo?.family_name}!`);

        // Guardian invitee → dashboard. The athlete profile already
        // exists in this family; guardians don't have their own profile,
        // so onboarding has nothing to ask them. Backend already set
        // users.user_type from invitation.anticipated_user_type.
        // Athlete invitee → quickstart so they can create their profile.
        setTimeout(async () => {
          clearInvitation();
          if (result.user_type === 'guardian') {
            navigate('/dashboard');
            return;
          }
          try {
            const onboardingData = await userAPI.getOnboardingStep(sessionToken);
            if (onboardingData.onboarding_complete || onboardingData.onboarding_step >= 100) {
              navigate('/dashboard');
            } else {
              navigate('/onboarding/quickStart');
            }
          } catch (e) {
            navigate('/onboarding/quickStart');
          }
        }, 1200);
      }
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      setHasProcessed(false);
    }
  };

  const handleSwitchAccount = async () => {
    // Soft-clear the invitation token so the next login picks it up from
    // localStorage (InvitationContext restores from there).
    try { await logout?.(); } catch {}
    navigate('/login');
  };

  const handleDeclineInvitation = () => {
    clearInvitation();
    navigate('/login');
  };

  const handleContinueToApp = () => {
    clearInvitation();
    navigate('/dashboard');
  };

  // Show error state
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafbff',
        padding: isMobile ? '12px' : '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          maxWidth: isMobile ? '100%' : '600px',
          width: '100%',
          padding: isMobile ? '24px' : '48px',
          textAlign: 'center'
        }}>          <div style={{ fontSize: '64px', marginBottom: '24px', color: '#F59E0B' }}>!</div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#1F2937',
            marginBottom: '12px',
            fontFamily: 'Manrope, sans-serif'
          }}>
            Invitation Error
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#F9550D',
            fontWeight: '600',
            marginBottom: '16px',
            fontFamily: 'Manrope, sans-serif'
          }}>
            {error}
          </p>
          <p style={{
            fontSize: '16px',
            color: '#6B7280',
            lineHeight: '1.6',
            marginBottom: '32px',
            fontFamily: 'Manrope, sans-serif'
          }}>
            {error.includes('already part of a family')
              ? "Already part of a family account. This invitation cannot be used."
              : "Continue with setting up a new family account."}
          </p>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleDeclineInvitation}
              style={{
                flex: 1,
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#6B7280',
                background: 'white',
                border: '1px solid #D1D5DB',
                borderRadius: '12px',
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Dismiss
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                flex: 1,
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                background: '#F9550D',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Continue Setup
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user already has a family
  const userHasFamily = user?.family_id && user?.family?.name;
  if (userHasFamily && isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafbff',
        padding: isMobile ? '12px' : '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          maxWidth: isMobile ? '100%' : '600px',
          width: '100%',
          padding: isMobile ? '24px' : '48px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '24px', width: '64px', height: '64px', borderRadius: '50%', background: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <span style={{ color: '#6B7280', fontWeight: '700' }}>?</span>
          </div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#1F2937',
            marginBottom: '12px',
            fontFamily: 'Manrope, sans-serif'
          }}>
            Already in a Family
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#F9550D',
            fontWeight: '600',
            marginBottom: '16px',
            fontFamily: 'Manrope, sans-serif'
          }}>
            Cannot Join Another Family
          </p>
          <p style={{
            fontSize: '16px',
            color: '#6B7280',
            lineHeight: '1.6',
            marginBottom: '32px',
            fontFamily: 'Manrope, sans-serif'
          }}>
            You're already part of "{user?.family?.name}". Users can only be part of one family at a time.
          </p>

          <button
            onClick={handleContinueToApp}
            style={{
              width: '100%',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              background: '#F9550D',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif'
            }}
          >
            Continue to App
          </button>
        </div>
      </div>
    );
  }

  // User NOT authenticated - show sign in/up options
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafbff',
        padding: isMobile ? '12px' : '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          maxWidth: isMobile ? '100%' : '600px',
          width: '100%',
          padding: isMobile ? '24px' : '48px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '24px', width: '64px', height: '64px', borderRadius: '50%', background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <span style={{ color: '#F59E0B', fontWeight: '700' }}>!</span>
          </div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#1F2937',
            marginBottom: '12px',
            fontFamily: 'Manrope, sans-serif'
          }}>
            You're Invited!
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#F9550D',
            fontWeight: '600',
            marginBottom: '16px',
            fontFamily: 'Manrope, sans-serif'
          }}>
            {invitationInfo?.family_name ? `Join ${invitationInfo.family_name}` : 'Join Family'}
          </p>
          <p style={{
            fontSize: '16px',
            color: '#6B7280',
            lineHeight: '1.6',
            marginBottom: '24px',
            fontFamily: 'Manrope, sans-serif'
          }}>
            {invitationInfo?.invited_by
              ? `${invitationInfo.invited_by} sent an invitation to join the family account. Sign in or create an account to accept this invitation.`
              : "Invitation to join a family account. Sign in or create an account to accept this invitation."}
          </p>

          {invitationInfo?.message && (
            <div style={{
              background: '#E3F2FD',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '32px',
              textAlign: 'left'
            }}>
              <p style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1565C0',
                marginBottom: '8px',
                fontFamily: 'Manrope, sans-serif'
              }}>
                Personal Message:
              </p>
              <p style={{
                fontSize: '16px',
                color: '#1976D2',
                fontStyle: 'italic',
                lineHeight: '1.4',
                fontFamily: 'Manrope, sans-serif'
              }}>
                "{invitationInfo.message}"
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleDeclineInvitation}
              style={{
                flex: 1,
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#6B7280',
                background: 'white',
                border: '1px solid #D1D5DB',
                borderRadius: '12px',
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Decline
            </button>
            <button
              onClick={() => navigate('/login')}
              style={{
                flex: 1,
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                background: '#F9550D',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Email mismatch was previously a hard block. Now a soft warn inline
  // on the acceptance screen below — Apple Hide-My-Email + multi-account
  // users would be trapped by a block since their Auth0 email isn't the
  // invited address. The accept screen renders the warning + confirm.

  // User IS authenticated - show acceptance screen
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#fafbff',
      padding: isMobile ? '12px' : '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: isMobile ? '0' : '16px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        maxWidth: isMobile ? '100%' : '600px',
        width: '100%',
        padding: isMobile ? '24px' : '48px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '32px', marginBottom: '24px', width: '64px', height: '64px', borderRadius: '50%', background: isProcessing ? '#E5E7EB' : hasProcessed ? '#D1FAE5' : '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
          <span style={{ color: isProcessing ? '#6B7280' : hasProcessed ? '#059669' : '#F59E0B', fontWeight: '700', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {isProcessing ? '...' : hasProcessed ? <Check size={30} aria-label="Invitation accepted" /> : '!'}
          </span>
        </div>
        
        <h1 style={{
          fontSize: '28px',
          fontWeight: '600',
          color: '#1F2937',
          marginBottom: '12px',
          fontFamily: 'Manrope, sans-serif'
        }}>
          {isProcessing ? 'Processing Invitation...' : 
           hasProcessed ? 'Welcome to the Family!' : 
           "You're Invited!"}
        </h1>
        
        <p style={{
          fontSize: '18px',
          color: '#F9550D',
          fontWeight: '600',
          marginBottom: '16px',
          fontFamily: 'Manrope, sans-serif'
        }}>
          {isProcessing ? 'Setting up the family account...' :
           hasProcessed ? `Successfully joined ${invitationInfo?.family_name}!` :
           `Join ${invitationInfo?.family_name}`}
        </p>
        
        <p style={{
          fontSize: '16px',
          color: '#6B7280',
          lineHeight: '1.6',
          marginBottom: '24px',
          fontFamily: 'Manrope, sans-serif'
        }}>
          {isProcessing ? 'This will only take a moment.' :
           hasProcessed ? "The family account is ready! Continue to the dashboard." :
           invitationInfo?.invited_by
             ? `${invitationInfo.invited_by} invited you to join ${invitationInfo?.family_name || 'this family account'} on APS Recruits.`
             : 'You\'ve been invited to join a family account on APS Recruits.'}
        </p>

        {/* Email-mismatch soft warn — show when the logged-in email
            doesn't match the invited address. User must explicitly
            acknowledge before they can Accept. Doesn't block accept
            entirely (Apple Hide-My-Email users would be permanently
            trapped). */}
        {!isProcessing && !hasProcessed && emailMismatch && (
          <div style={{
            textAlign: 'left',
            background: '#FEF3C7',
            border: '1px solid #FCD34D',
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            fontFamily: 'Manrope, sans-serif',
          }}>
            <p style={{ margin: 0, marginBottom: 8, fontSize: 13, color: '#92400E', fontWeight: 700 }}>
              This invite was sent to a different email
            </p>
            <p style={{ margin: 0, marginBottom: 12, fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>
              Sent to <strong>{invitationInfo?.email}</strong>. You're signed in as <strong>{user?.email}</strong>. If this is the right account, continue. Or log in with the invited address.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#151133', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={mismatchAcknowledged}
                onChange={(e) => setMismatchAcknowledged(e.target.checked)}
              />
              Yes, accept with <strong>{user?.email}</strong>
            </label>
            <button
              onClick={handleSwitchAccount}
              style={{
                marginTop: 10,
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#F9550D',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif',
                textDecoration: 'underline',
              }}
            >
              Use the invited email instead
            </button>
          </div>
        )}

        {invitationInfo?.message && !isProcessing && !hasProcessed && (
          <div style={{
            background: '#E3F2FD',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '32px',
            textAlign: 'left'
          }}>
            <p style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1565C0',
              marginBottom: '8px',
              fontFamily: 'Manrope, sans-serif'
            }}>
              Personal Message:
            </p>
            <p style={{
              fontSize: '16px',
              color: '#1976D2',
              fontStyle: 'italic',
              lineHeight: '1.4',
              fontFamily: 'Manrope, sans-serif'
            }}>
              "{invitationInfo.message}"
            </p>
          </div>
        )}

        {isProcessing && (
          <div style={{ margin: '24px 0' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #F3F4F6',
              borderTop: '4px solid #F9550D',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
            <style>
              {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
            </style>
          </div>
        )}

        {/* Role chooser — invitee picks Athlete or Parent/Guardian.
            Routes guardians inline to the dashboard with the name/phone
            intake below; athletes continue to /onboarding/quickStart. */}
        {!isProcessing && !hasProcessed && (
          <div style={{ marginBottom: isGuardianInvite ? 16 : 24 }}>
            <p style={{ fontSize: 13, color: '#6B7280', fontFamily: 'Manrope, sans-serif', marginBottom: 10, textAlign: 'left' }}>
              Are you the athlete or a parent / guardian?
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              {(['athlete', 'guardian'] as const).map((role) => {
                const active = selectedRole === role;
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setSelectedRole(role)}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      fontSize: 14,
                      fontWeight: 700,
                      color: active ? 'white' : '#151133',
                      background: active ? '#F9550D' : 'white',
                      border: `1px solid ${active ? '#F9550D' : '#D1D5DB'}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontFamily: 'Manrope, sans-serif',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {role === 'athlete' ? "I'm the athlete" : "I'm a parent / guardian"}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!isProcessing && !hasProcessed && isGuardianInvite && (
          <div style={{ textAlign: 'left', marginBottom: 24 }}>
            <p style={{ fontSize: 13, color: '#6B7280', fontFamily: 'Manrope, sans-serif', marginBottom: 12 }}>
              A few quick details so coaches know who you are.
            </p>
            {/* flex:1 on the name pair and the fixed 44px height are kept so
                the row does not reflow; the rest of the box — border, focus
                ring, invalid, disabled — is .aps-input's now. */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <TextInput
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                aria-label="First name"
                style={{ flex: 1, height: 44, padding: '0 14px' }}
              />
              <TextInput
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                aria-label="Last name"
                style={{ flex: 1, height: 44, padding: '0 14px' }}
              />
            </div>
            <TextInput
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone (optional)"
              aria-label="Phone"
              style={{ height: 44, padding: '0 14px' }}
            />
          </div>
        )}

        {/* Decline button removed — it was a no-op (just navigated
            away without touching the invitation row). Inviters can
            Cancel from their side; invitees just close the tab. */}
        {!isProcessing && !hasProcessed && (
          <button
            onClick={handleAcceptInvitation}
            disabled={isProcessing}
            style={{
              width: '100%',
              padding: '14px 24px',
              fontSize: '16px',
              fontWeight: 700,
              color: 'white',
              background: '#F9550D',
              border: 'none',
              borderRadius: '12px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              opacity: isProcessing ? 0.6 : 1,
              fontFamily: 'Manrope, sans-serif',
              boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
            }}
          >
            Accept invitation
          </button>
        )}
      </div>
    </div>
  );
};

export default InvitationWelcome;
